import { z } from "zod";

import { Form } from "@/components/ui/form";
import i18n from "@/i18n";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Input,
} from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  dateSchema,
  formatDate,
  parseToDateValue,
} from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { Tables } from "@/gen/tableNames/tables";

const FormSchema = z.object({
  strategy_from_id: z.coerce.number().nullable().optional(),
  strategy_to_id: z.coerce.number().nullable().optional(),
  quantity: z.coerce.number().nullable().optional(),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  weight: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  time_serie_file_id: z.coerce.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
const formSchemaFields = FormSchema.keyof().enum;

export function E2PositionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {i18n.t(`${Tables.E2_POSITIONS}.position`, {
                  defaultValue: "Position",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_from_id}
                    label={i18n.t(
                      `${Tables.E2_POSITIONS}.columns.strategy_from_name`,
                      {
                        ns: "tables",
                        defaultValue: "Strategy from ID",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_to_id}
                    label={i18n.t(
                      `${Tables.E2_POSITIONS}.columns.strategy_to_name`,
                      {
                        ns: "tables",
                        defaultValue: "Strategy to ID",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={i18n.t(`${Tables.E2_POSITIONS}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.quantity}
                    label={i18n.t(`${Tables.E2_POSITIONS}.columns.quantity`, {
                      ns: "tables",
                      defaultValue: "Quantity",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.weight}
                    label={i18n.t(`${Tables.E2_POSITIONS}.columns.weight`, {
                      ns: "tables",
                      defaultValue: "Weight",
                    })}
                    control={form.control}
                  />
                </div>

                <Divider />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_local_ccy}
                    label={i18n.t(
                      `${Tables.E2_POSITIONS}.columns.market_value_local_ccy`,
                      {
                        ns: "tables",
                        defaultValue: "Market value (local currency)",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_ptf_ccy}
                    label={i18n.t(
                      `${Tables.E2_POSITIONS}.columns.market_value_ptf_ccy`,
                      {
                        ns: "tables",
                        defaultValue: "Market value (portfolio currency)",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <Divider />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={i18n.t("technical_info.id", {
                      defaultValue: "Id",
                    })}
                    value={id.toString()}
                    readOnly
                  />
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={i18n.t("technical_info.created_at", {
                      defaultValue: "Créé le",
                    })}
                    value={formatDate(form.watch(formSchemaFields.created_at))}
                    readOnly
                  />
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={i18n.t("technical_info.updated_at", {
                      defaultValue: "Mis à jour le",
                    })}
                    value={formatDate(form.watch(formSchemaFields.updated_at))}
                    readOnly
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={i18n.t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, [], {}, table_name)
              : i18n.t("no_changes")}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
